import React from 'react';
import { OnActionArgs, OnEnterArgs, State } from '..';
import { IApi } from 'utils/API';
import { INotificationContext } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import { IBinLocation, IItemBinLocation } from 'interfaces';
import Translation from 'utils/Language/Translation';
import { MaybePromise, distinctBy } from 'component_utils/utils';
import { AutoCompleteFetcher } from 'components/Autocomplete';
import { locationAutocompleteOptions } from 'component_utils/objectUtils';

type FindLocation<S, P, R> = (state: OnActionArgs<S, P, R>) => Promise<IBinLocation>;

interface EventProps {
  api: IApi;
  notifications: INotificationContext;
}

interface Config<S, P, A, R> {
  nextState: number;
  binLocationKey: KeysOfType<S, IBinLocation>;
  binLocationContentsKey?: KeysOfType<S, IItemBinLocation[]>;
  attributes?: Partial<A>;
  findLocation?: FindLocation<S, P, R>;
  possibleLocations?: (state: OnEnterArgs<S, P, R>) => MaybePromise<IBinLocation[]>
}

const StateBinLocation = function <S, P extends EventProps, A extends { hint: string, autocompleter?: AutoCompleteFetcher }, R>({
  nextState,
  binLocationKey,
  attributes = {},
  findLocation = ({ props, inputs: [s] }) => props.api.binLocation.findBinLocation(s),
  possibleLocations,
  binLocationContentsKey
}: Config<S, P, A, R>): State<S, P, A, R> {
  return {
    onAction: async (args) => {
      const result = {} as any
      const location = await findLocation(args);
      if (!location) {
        throw args.props.notifications.add('danger', <Translation name="T.errors.unknownLocation" />);
      }
      result[binLocationKey] = location
      if (binLocationContentsKey) {
        result[binLocationContentsKey] = await args.props.api.binLocation.getBinLocationContents(location.code)
      }

      return {
        type: 'MOVE',
        data: result,
        nextState,
      };
    },
    attributes: async (args) => {
      const { props: { api } } = args
      let autocompleter = api.binLocation.findAutocomplete
      if (possibleLocations) {
        const locations = distinctBy(await possibleLocations(args), it => it.code)
        autocompleter = async s => locationAutocompleteOptions(locations, s) as any[]
      }

      return {
        hint: 'T.hints.location',
        autocompleter,
        ...(attributes as any),  
      }
    },

    nodeName: 'T.states.location',
    outputs: [
      {
        name: 'onScan',
        to: nextState,
      },
    ],
  };
};

export default StateBinLocation;
